<template>
  <div>
    <quill-editor
      ref="quillEditor"
      v-model="editorContent"
      :options="editorOptions"
      class="quill-editor-container"
      @change="onEditorChange"
      @ready="onEditorReady"
    />
  </div>
</template>

<script>
import { quillEditor, Quill } from 'vue-quill-editor';

// Register table formats
const BlockEmbed = Quill.import('blots/block/embed');

class TableBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.innerHTML = value;
    node.contentEditable = 'false'; // Make the table itself not editable
    // Make table cells editable
    node.querySelectorAll('td, th').forEach(cell => {
      cell.setAttribute('contenteditable', 'true');
      cell.style.cursor = 'text';
      cell.style.userSelect = 'text';
    });
    return node;
  }

  static value(node) {
    return node.innerHTML;
  }
}

TableBlot.blotName = 'table';
TableBlot.tagName = 'table';

Quill.register(TableBlot);

export default {
  name: 'HtmlRichEditorComp',
  components: {
    quillEditor,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Enter text...',
    },
  },
  data() {
    return {
      editorContent: '',
      quill: null,
      editorOptions: {
        placeholder: this.placeholder,
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline'],
              [{ 'list': 'ordered' }, { 'list': 'bullet' }],
              [{ 'header': [1, 2, 3, false] }],
              [{ 'align': [] }],
              ['link'],
              ['insertTable'],
              ['clean']
            ],
            handlers: {
              insertTable: this.insertTable
            }
          },
          clipboard: {
            matchVisual: false
          }
        },
        formats: [
          'bold', 'italic', 'underline',
          'list', 'bullet',
          'header',
          'align',
          'link',
          'table'
        ]
      }
    };
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal !== this.editorContent) {
          this.editorContent = this.decodeHtmlEntities(newVal);
          // Make table cells editable after content is loaded
          this.$nextTick(() => {
            this.makeTableCellsEditable();
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    onEditorReady(quill) {
      this.quill = quill;
      // Add custom button icon and styling
      const insertTableButton = document.querySelector('.ql-insertTable');
      if (insertTableButton) {
        insertTableButton.innerHTML = '<svg viewbox="0 0 18 18"><rect class="ql-stroke" height="12" width="12" x="3" y="3"></rect><line class="ql-stroke" x1="3" x2="15" y1="9" y2="9"></line><line class="ql-stroke" x1="9" x2="9" y1="3" y2="15"></line></svg>';
        insertTableButton.setAttribute('title', 'Insert Table');
      }
      
      // Enable table cell editing
      this.setupTableEditing();
    },
    setupTableEditing() {
      if (!this.quill) return;
      
      // Make existing table cells editable
      this.makeTableCellsEditable();
    },
    makeTableCellsEditable() {
      if (!this.quill) return;
      
      const tables = this.quill.root.querySelectorAll('table');
      tables.forEach(table => {
        const cells = table.querySelectorAll('td, th');
        cells.forEach(cell => {
          cell.setAttribute('contenteditable', 'true');
          cell.style.cursor = 'text';
        });
      });
    },
    onEditorChange() {
      this.$emit('input', this.editorContent);
    },
    insertTable() {
      if (!this.quill) return;
      
      const range = this.quill.getSelection(true);
      
      // Create a simple 3x3 table with headers
      const tableHTML = `
        <table>
          <thead>
            <tr>
              <th></th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      `;
      
      // Insert the table at cursor position
      this.quill.clipboard.dangerouslyPasteHTML(range.index, tableHTML);
      
      // Make the newly inserted table cells editable
      this.$nextTick(() => {
        this.makeTableCellsEditable();
      });
      
      // Move cursor after the table
      this.quill.setSelection(range.index + 1);
    },
    decodeHtmlEntities(html) {
      if (!html) return html;
      const textarea = document.createElement('textarea');
      textarea.innerHTML = html;
      let decoded = textarea.value;
      
      // Remove wrapping <p> tags around tables and clean up the structure
      decoded = decoded.replace(/<p>\s*<table>/gi, '<table>');
      decoded = decoded.replace(/<\/table>\s*<\/p>/gi, '</table>');
      
      // Remove <p> tags around table elements
      decoded = decoded.replace(/<p>\s*<(thead|tbody|tr|th|td)/gi, '<$1');
      decoded = decoded.replace(/<\/(thead|tbody|tr|th|td)>\s*<\/p>/gi, '</$1>');
      
      // Remove empty <p></p> tags
      decoded = decoded.replace(/<p>\s*<\/p>/gi, '');
      
      return decoded;
    },
  },
};
</script>

<style>
@import '~quill/dist/quill.core.css';
@import '~quill/dist/quill.snow.css';

.quill-editor-container {
  min-height: 200px;
}

.ql-container {
  min-height: 200px;
  font-size: 14px;
}

.ql-editor {
  min-height: 200px;
}

/* Light Theme Styles */
.theme--light .ql-toolbar.ql-snow {
  border: 1px solid #ccc;
  background-color: #ffffff;
}

.theme--light .ql-container.ql-snow {
  border: 1px solid #ccc;
  background-color: #ffffff;
}

.theme--light .ql-editor {
  color: #000000;
}

.theme--light .ql-editor.ql-blank::before {
  color: rgba(0, 0, 0, 0.6);
}

/* Light theme table styling */
.theme--light .ql-editor table td,
.theme--light .ql-editor table th {
  border: 1px solid #ddd;
}

.theme--light .ql-editor table th {
  background-color: #f1f3f4;
  font-weight: bold;
  text-align: left;
  color: #000000;
}

.theme--light .ql-editor table tr:hover {
  background-color: #f5f5f5;
}

/* Dark Theme Styles */
.theme--dark .ql-toolbar.ql-snow {
  border: 1px solid #525252;
  background-color: #383838;
}

.theme--dark .ql-toolbar.ql-snow .ql-stroke {
  stroke: #ffffff;
}

.theme--dark .ql-toolbar.ql-snow .ql-fill {
  fill: #ffffff;
}

.theme--dark .ql-toolbar.ql-snow .ql-picker-label {
  color: #ffffff;
}

.theme--dark .ql-toolbar.ql-snow .ql-picker-options {
  background-color: #383838;
  border: 1px solid #525252;
}

.theme--dark .ql-toolbar.ql-snow button:hover,
.theme--dark .ql-toolbar.ql-snow button:focus,
.theme--dark .ql-toolbar.ql-snow button.ql-active {
  color: #f89554;
}

.theme--dark .ql-toolbar.ql-snow button:hover .ql-stroke,
.theme--dark .ql-toolbar.ql-snow button:focus .ql-stroke,
.theme--dark .ql-toolbar.ql-snow button.ql-active .ql-stroke {
  stroke: #f89554;
}

.theme--dark .ql-toolbar.ql-snow button:hover .ql-fill,
.theme--dark .ql-toolbar.ql-snow button:focus .ql-fill,
.theme--dark .ql-toolbar.ql-snow button.ql-active .ql-fill {
  fill: #f89554;
}

.theme--dark .ql-container.ql-snow {
  border: 1px solid #525252;
  background-color: #383838;
}

.theme--dark .ql-editor {
  color: #ffffff;
}

.theme--dark .ql-editor.ql-blank::before {
  color: rgba(255, 255, 255, 0.6);
}

/* Dark theme table styling */
.theme--dark .ql-editor table td,
.theme--dark .ql-editor table th {
  border: 1px solid #525252;
  color: #ffffff;
}

.theme--dark .ql-editor table th {
  background-color: #525252;
  font-weight: bold;
  text-align: left;
  color: #ffffff;
}

.theme--dark .ql-editor table tr:hover {
  background-color: #404040;
}

.theme--dark .ql-editor table tbody tr {
  background-color: #383838;
}

/* Common table styling */
.ql-editor table {
  border-collapse: collapse;
  width: 100%;
  margin: 0.5rem 0 0 0;
}

.ql-editor table td,
.ql-editor table th {
  padding: 8px;
  min-width: 50px;
}

/* Make table cells editable */
.ql-editor table {
  user-select: none;
}

.ql-editor table td,
.ql-editor table th {
  cursor: text !important;
  user-select: text !important;
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  -ms-user-select: text !important;
}

.ql-editor table td[contenteditable="true"],
.ql-editor table th[contenteditable="true"] {
  outline: none;
  cursor: text !important;
  pointer-events: auto !important;
}

.ql-editor table td *,
.ql-editor table th * {
  cursor: text;
  user-select: text;
}

/* Override Quill's default table behavior */
.ql-editor table {
  pointer-events: auto;
}
</style>
